import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function GarageDoor() {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            setIsLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isImageModalOpen) {
                setIsImageModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isImageModalOpen]);

    if (!isLoaded) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
                    <p className="text-brand-green">Loading...</p>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-brand-cream" style={{ opacity: 1 }}>
            {/* Hero Section */}
            <section className="bg-brand-green text-white py-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h1
                        className="text-4xl md:text-6xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Premium Garage Doors
                    </motion.h1>
                    <motion.p
                        className="text-xl md:text-2xl max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        Quality installation, repair, and maintenance services for security and convenience
                    </motion.p>
                </div>
            </section>

            {/* Garage Door Types Section */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Our Garage Door Types
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Choose from our range of premium garage door styles, each designed for durability, security, and aesthetic appeal
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Smooth Panel */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className="mb-4">
                                <h3 className="text-2xl font-bold text-black mb-3">Smooth Panel</h3>
                                <p className="text-gray-700 mb-4">
                                    Sleek, modern design with clean lines perfect for contemporary architecture.
                                </p>
                                <ul className="space-y-2 text-gray-700">
                                    <li className="flex items-start">
                                        <span className="text-brand-orange mr-2">•</span>
                                        <span>Clean, seamless appearance</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-brand-orange mr-2">•</span>
                                        <span>Excellent weather resistance</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-brand-orange mr-2">•</span>
                                        <span>Low maintenance</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-80">
                                <img
                                    src={require('../images/gallery/garage-2.jpg')}
                                    alt="Smooth panel garage door"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>

                        {/* Flat Embossed Panel */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div className="mb-4">
                                <h3 className="text-2xl font-bold text-black mb-3">Flat Embossed Panel</h3>
                                <p className="text-gray-700 mb-4">
                                    Classic, textured design that adds depth and character with traditional elegance.
                                </p>
                                <ul className="space-y-2 text-gray-700">
                                    <li className="flex items-start">
                                        <span className="text-brand-orange mr-2">•</span>
                                        <span>Textured, embossed surface</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-brand-orange mr-2">•</span>
                                        <span>Classic architectural appeal</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-brand-orange mr-2">•</span>
                                        <span>Enhanced durability</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-80">
                                <img
                                    src={require('../images/gallery/garage-1.jpg')}
                                    alt="Flat embossed panel garage door"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Accessories Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Accessories
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Enhance your garage door system with our range of accessories
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg text-center"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-2">Weather Seal</h3>
                            <p className="text-gray-700">Blocks drafts, dust, and moisture for better protection</p>
                        </motion.div>

                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg text-center"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-2">Remotes</h3>
                            <p className="text-gray-700">Multi-button, secure, and convenient remote controls</p>
                        </motion.div>

                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg text-center"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-2">Wi-Fi Control</h3>
                            <p className="text-gray-700">Operate and monitor your door from anywhere</p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Color Scheme Section */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Color Options
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Explore our wide range of color options to find the perfect match for your home's exterior
                        </p>
                    </motion.div>

                    <motion.div
                        className="max-w-5xl mx-auto"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                            <img
                                src={require('../images/instructions/color-scheme.jpg')}
                                alt="Garage door color scheme options"
                                className="w-full h-auto object-contain"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Installation & Resources Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Installation & Resources
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Essential guides and documentation for builders, installers, and homeowners
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Builder Fixing Details */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">
                                Builder Fixing Details
                            </h3>
                            <p className="text-gray-700 mb-6">
                                Detailed installation instructions and fixing specifications for builders and installers.
                                This guide shows proper mounting points, hardware requirements, and installation procedures.
                                <span className="block mt-2 text-sm text-brand-orange font-medium">Click image to enlarge</span>
                            </p>
                            <div
                                className="bg-white rounded-xl overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-300"
                                onClick={() => setIsImageModalOpen(true)}
                            >
                                <img
                                    src={require('../images/instructions/builder-fixing-details.jpg')}
                                    alt="Garage door builder fixing details and installation instructions"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </motion.div>

                        {/* Garage Door Opener User Guide */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">
                                Garage Door Opener User Guide
                            </h3>
                            <p className="text-gray-700 mb-6">
                                Comprehensive user guide for garage door openers. Includes installation instructions,
                                programming steps, troubleshooting tips, and maintenance recommendations to help you
                                get the most out of your garage door system.
                            </p>
                            <a
                                href="/documents/garagedoor-opener-guide.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange-hover transition-colors duration-300 shadow-md hover:shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download User Guide (PDF)
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Image Modal */}
            <AnimatePresence>
                {isImageModalOpen && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsImageModalOpen(false)}
                    >
                        <motion.div
                            className="relative max-w-7xl max-h-[90vh] bg-white rounded-lg overflow-hidden shadow-2xl"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close Button */}
                            <button
                                className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                                onClick={() => setIsImageModalOpen(false)}
                                aria-label="Close image"
                            >
                                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            {/* Image */}
                            <div className="overflow-auto max-h-[90vh]">
                                <img
                                    src={require('../images/instructions/builder-fixing-details.jpg')}
                                    alt="Garage door builder fixing details and installation instructions - enlarged view"
                                    className="w-full h-auto"
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default GarageDoor;

